import { ChangeEventHandler, useState } from 'react'

import { LoadingButton } from '@mui/lab'
import { Button, InputBase, Paper, Stack, SvgIcon, styled } from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/dist/query'

import { Typo } from '~/core/components/alias'
import { GirlDoze } from '~/core/components/girl-doze'
import { Title } from '~/core/components/title'
import { safeParseInt } from '~/core/utils/safe-parse-int'
import { SearchItem } from '~/features/guild/contract/types'

import { ReactComponent as SearchIcon } from '/assets/icons/search.svg'

import { GuildApi } from '~/services/fm/guild'
import CenterCircularProgress from '~/shared/components/center-circular-progress'
import AppLink from '~/shared/navigation/app-link'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'

const SPACING_NO_DATA = 9.5

module S {
  export const SearchBox = styled(Stack)`
    align-items: center;
    height: 3rem;
    padding-left: 1rem;
    border-radius: 1.2rem;
    font-size: 1.2rem;
    background-color: ${({ theme }) => theme.perMode('hsl(0, 0%, 96%)', 'hsl(0, 0%, 13%)')};
  `

  export const SearchInput = styled(InputBase)`
    position: relative;
    top: 0.1rem;
    width: 100%;
    font-size: 1.2rem;
  `

  export const SearchBtn = styled(LoadingButton)`
    height: 3rem;
  `
}

module C {
  export const Loading = () => <CenterCircularProgress />

  export const NoResult = () => (
    <Stack alignItems="center" width={1}>
      <GirlDoze $width="18.5rem" />
      <Typo fontSize="1.2rem" color="text.secondary">
        暂无结果
      </Typo>
    </Stack>
  )

  export const Search = (props: { loading: boolean; value: number | undefined; onSearch: (id: number) => void }) => {
    const [value, setValue] = useState(props.value)

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      const trim = e.target.value.trim()
      if (trim === '') {
        setValue(undefined)
        return
      }
      const int = Number.parseInt(trim)
      if (Number.isSafeInteger(int)) {
        setValue(int)
      }
    }

    const handleSearch = () => {
      if (value !== undefined) {
        props.onSearch(value)
      }
    }

    return (
      <Stack spacing={0.5} direction="row">
        <S.SearchBox direction="row" flexGrow={1} spacing={0.5}>
          <SvgIcon inheritViewBox component={SearchIcon} sx={{ fontSize: '1.6rem' }} />
          <S.SearchInput
            value={value ?? ''}
            placeholder="请输入公会长M号"
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch()
              }
            }}
          />
        </S.SearchBox>

        <S.SearchBtn variant="text" disableRipple loading={props.loading} onClick={handleSearch}>
          <Typo color="text.primary" fontSize="1.5rem">
            搜索
          </Typo>
        </S.SearchBtn>
      </Stack>
    )
  }

  export const Card = (props: { guild: SearchItem }) => (
    <Paper elevation={0}>
      <Stack alignItems="center" spacing={2} py={2.5}>
        <Stack alignItems="center" spacing={0.5}>
          <Typo fontSize="1.4rem">{props.guild.guild_name}</Typo>
          <Typo fontSize="1.2rem" color="text.secondary">
            公会长M号：{props.guild.user_id}
          </Typo>
        </Stack>
        <Button
          variant="contained"
          component={AppLink}
          to={`/user/guild/join?owner_id=${props.guild.user_id}`}
          sx={{ width: '10rem', height: '3.2rem', fontSize: '1.2rem' }}
        >
          申请加入
        </Button>
      </Stack>
    </Paper>
  )
}

export const GuildSearch = () => {
  const [search, setSearch] = useAppSearchParam()
  const s = search.get('s') ?? ''

  const ownerId = safeParseInt(s)
  const { ...searchGuild } = GuildApi.searchGuild.useQuery(ownerId ? { ownerId } : skipToken)

  const handleSearch = (id: number) => {
    setSearch({ s: id.toString() })
  }

  return (
    <>
      <Title>加入公会</Title>
      <Stack spacing={searchGuild.data?.length ? 2 : SPACING_NO_DATA}>
        <C.Search loading={searchGuild.isFetching} value={ownerId} onSearch={handleSearch} />
        {searchGuild.isFetching ? (
          <C.Loading />
        ) : s && !searchGuild.data?.length ? (
          <C.NoResult />
        ) : (
          searchGuild.data?.map((x) => <C.Card key={x.id} guild={x} />)
        )}
      </Stack>
    </>
  )
}
